import React, { useEffect, useState } from 'react';
import { KeyRound, ShieldCheck, Copy, Check, RefreshCw, Loader2 } from 'lucide-react';
import { listScheduledPosts, ScheduledPost } from '@/lib/brandStudio';
import ApprovalStep from '@/components/zera/ApprovalStep';
import { GlassPanel } from './ui';

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const CODE_KEY = 'mtnadmin_suite_code';

const getOrCreateCode = (): string => {
  try {
    const existing = sessionStorage.getItem(CODE_KEY);
    if (existing) return existing;
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    sessionStorage.setItem(CODE_KEY, code);
    return code;
  } catch {
    return '000000';
  }
};

/**
 * Administration Suite — Admin Approval area. After admin authentication a
 * temporary six-digit access code is issued for frictionless re-entry.
 */
const AdminSuite: React.FC = () => {
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [code] = useState<string>(getOrCreateCode());
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setPosts(await listScheduledPosts());
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const copyCode = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="space-y-8">
      {/* Six-digit access code */}
      <GlassPanel className="flex flex-wrap items-center justify-between gap-6 p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C9A961]/15">
            <KeyRound className="h-5 w-5 text-[#E4C77F]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Temporary Access Code</div>
            <p className="mt-1 text-sm text-white/50">
              Use this six-digit code to re-enter the Administration Suite without retyping your credentials.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {code.split('').map((d, i) => (
              <div key={i} className="flex h-12 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] font-mono text-xl text-white">
                {d}
              </div>
            ))}
          </div>
          <button onClick={copyCode} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-3 text-xs text-white/70 hover:text-white">
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </GlassPanel>

      {/* Approval board — rendered on a light surface inside the dark suite */}
      <GlassPanel className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Admin Approval</div>
              <h3 className="mt-0.5 text-xl font-light text-white">Review &amp; Approve Posts</h3>
            </div>
          </div>
          <button onClick={refresh} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/60 hover:text-white">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
        <div className="bg-white p-6">
          {loading ? (
            <div className="flex items-center justify-center gap-3 py-20 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading approval queue…
            </div>
          ) : (
            <ApprovalStep posts={posts} refresh={refresh} fmtDateTime={fmtDateTime} goToStep={() => {}} />
          )}
        </div>
      </GlassPanel>
    </div>
  );
};

export default AdminSuite;
